import { getLevelApiValue } from '@/school-management/utils/bulkLevelChange';

/**
 * Shared logic for the three per-class level control settings introduced in ELL-7746:
 * placementTestRequired, defaultStartingLevel, allowIndividualAdvancement.
 * See backend contract in ELL-7727.
 */

export interface LevelControlValues {
  placementTestRequired: boolean;
  defaultStartingLevel: string | null;
  allowIndividualAdvancement: boolean;
}

export const LEVEL_CONTROL_DEFAULTS: LevelControlValues = {
  placementTestRequired: true,
  defaultStartingLevel: null,
  allowIndividualAdvancement: true,
};

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * The copy lives once, under the admin namespace (`admin.schoolDetailsPage.classes.levelControl`
 * in src/locales/en/translation.ts). Admin components use `useAdminTranslation()`, whose 'admin'
 * namespace already resolves keys without that prefix. Teacher components use the default
 * `useTranslation()`, which needs the leading `admin.` to reach the same strings - so callers on
 * that side must pass `keyPrefix: 'admin.'`.
 */
const NS = (keyPrefix: string) => `${keyPrefix}schoolDetailsPage.classes.levelControl`;

/**
 * System level ids, matching the existing `levelName_1`..`levelName_5` catalog.
 * There are only five levels - the 14-item school-grade catalog is a different thing entirely
 * and must not be used here (see getLevelControlOptions).
 */
export const LEVEL_CONTROL_LEVEL_IDS = [1, 2, 3, 4, 5] as const;

/**
 * Normalizes `defaultStartingLevel` as returned by the API into the exact string form the
 * dropdown option values use (`LEVEL_1`..`LEVEL_5`).
 *
 * The backend serializes `UserLevel` through EnumMemberConverter, so it comes back as the
 * EnumMember string ('LEVEL_3'), not a number. Older/other callers may still hand us a bare
 * id (3 or '3'), so both shapes are folded into the canonical `LEVEL_n` form - otherwise the
 * current level silently fails to preselect when editing a class.
 */
export const normalizeStartingLevel = (
  value: string | number | null | undefined
): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value);
  if (raw.startsWith('LEVEL_')) return raw;
  const id = Number(raw);
  return Number.isFinite(id) ? getLevelApiValue(id) : null;
};

/**
 * Extracts the numeric level id from any accepted shape (`'LEVEL_3'`, `'3'`, `3`) - the inverse of
 * `getLevelApiValue`. Callers that need a number (e.g. presetting a dashboard level) must use this
 * rather than `Number(normalizeStartingLevel(x))`, which is NaN for the canonical `LEVEL_n` form.
 */
export const getLevelIdFromValue = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value);
  const id = Number(raw.startsWith('LEVEL_') ? raw.slice('LEVEL_'.length) : raw);
  return Number.isFinite(id) ? id : null;
};

/**
 * The five system levels. Values are the `LEVEL_1`..`LEVEL_5` enum strings the backend's
 * `UserLevel` converter accepts - it throws on a bare number or numeric string, so sending
 * the raw grade id fails the request outright. Labels reuse the `levelName_n` catalog already
 * used elsewhere (e.g. NotificationsContext level-change messages).
 */
export const getLevelControlOptions = (t: TranslateFn) =>
  LEVEL_CONTROL_LEVEL_IDS.map(id => ({
    label: t(`levelName_${id}`),
    value: getLevelApiValue(id),
  }));

/**
 * Applies the "reset defaultStartingLevel to null when placement test is turned back on" rule.
 */
export const applyPlacementTestToggle = (
  values: LevelControlValues,
  placementTestRequired: boolean
): LevelControlValues => ({
  ...values,
  placementTestRequired,
  defaultStartingLevel: placementTestRequired ? null : values.defaultStartingLevel,
});

/** Whether the form can be submitted given the current level control values. */
export const isLevelControlValid = (values: LevelControlValues): boolean =>
  values.placementTestRequired || Boolean(values.defaultStartingLevel);

/**
 * Single-setting warning message builders, used to confirm each change as it happens.
 * `isNewClass` picks between the "new class" and "existing class" copy variants (ELL-7955) -
 * new classes have no existing students yet, so their warnings omit any mention of them.
 * Pass keyPrefix: 'admin.' when calling with a `t` from plain `useTranslation()` (teacher side).
 * Leave it empty (default) when calling with `useAdminTranslation()`'s `t` (admin side).
 */
export const getPlacementTestWarning = (
  nextValue: boolean,
  isNewClass: boolean,
  t: TranslateFn,
  keyPrefix = ''
): string =>
  nextValue
    ? t(
        `${NS(keyPrefix)}.warnings.placementTestTurnedOn${isNewClass ? 'NewClass' : 'ExistingClass'}`
      )
    : t(
        `${NS(keyPrefix)}.warnings.placementTestTurnedOff${isNewClass ? 'NewClass' : 'ExistingClass'}`
      );

export const getDefaultStartingLevelWarning = (
  isNewClass: boolean,
  t: TranslateFn,
  keyPrefix = ''
): string =>
  t(`${NS(keyPrefix)}.warnings.defaultLevelChanged${isNewClass ? 'NewClass' : 'ExistingClass'}`);

/**
 * Turning progression off reads the same regardless of new/existing class, so it has a single
 * key; only the "turned on" direction differentiates (a new class can't have students who
 * already completed a level).
 */
export const getAdvancementWarning = (
  nextValue: boolean,
  isNewClass: boolean,
  t: TranslateFn,
  keyPrefix = ''
): string =>
  nextValue
    ? t(`${NS(keyPrefix)}.warnings.advancementTurnedOn${isNewClass ? 'NewClass' : 'ExistingClass'}`)
    : t(`${NS(keyPrefix)}.warnings.advancementTurnedOff`);

/**
 * Confirm-button label builders, paired with the warning builders above so each of the three
 * level control actions gets an action-specific label (e.g. "Disable Level Test") instead of a
 * generic "Confirm". See ELL-8004.
 */
export const getPlacementTestConfirmLabel = (
  nextValue: boolean,
  t: TranslateFn,
  keyPrefix = ''
): string =>
  t(
    `${NS(keyPrefix)}.${nextValue ? 'confirmButtonEnableLevelTest' : 'confirmButtonDisableLevelTest'}`
  );

export const getDefaultStartingLevelConfirmLabel = (
  isNewClass: boolean,
  t: TranslateFn,
  keyPrefix = ''
): string =>
  t(`${NS(keyPrefix)}.${isNewClass ? 'confirmButtonSetLevel' : 'confirmButtonChangeLevel'}`);

export const getAdvancementConfirmLabel = (
  nextValue: boolean,
  t: TranslateFn,
  keyPrefix = ''
): string =>
  t(
    `${NS(keyPrefix)}.${
      nextValue ? 'confirmButtonEnableLevelProgression' : 'confirmButtonDisableLevelProgression'
    }`
  );

/**
 * Builds the ordered list of "Are you sure?" warning messages for whichever of the three
 * settings changed since the values were originally loaded. Multiple messages are
 * concatenated/sequenced by the caller in a single modal. Kept as a fallback diff for any
 * caller that batches changes instead of confirming each one as it happens.
 */
export const getLevelControlWarnings = (
  prev: LevelControlValues,
  next: LevelControlValues,
  isNewClass: boolean,
  t: TranslateFn,
  keyPrefix = ''
): string[] => {
  const messages: string[] = [];

  if (prev.placementTestRequired !== next.placementTestRequired) {
    messages.push(getPlacementTestWarning(next.placementTestRequired, isNewClass, t, keyPrefix));
  } else if (
    !prev.placementTestRequired &&
    !next.placementTestRequired &&
    prev.defaultStartingLevel !== next.defaultStartingLevel
  ) {
    // Only fires when the placement test was already off in both states, otherwise the
    // placementTestTurnedOff/On message above already covers the default level reassignment.
    messages.push(getDefaultStartingLevelWarning(isNewClass, t, keyPrefix));
  }

  if (prev.allowIndividualAdvancement !== next.allowIndividualAdvancement) {
    messages.push(getAdvancementWarning(next.allowIndividualAdvancement, isNewClass, t, keyPrefix));
  }

  return messages;
};

export const hasLevelControlChanges = (
  prev: LevelControlValues,
  next: LevelControlValues
): boolean =>
  prev.placementTestRequired !== next.placementTestRequired ||
  prev.defaultStartingLevel !== next.defaultStartingLevel ||
  prev.allowIndividualAdvancement !== next.allowIndividualAdvancement;
